import React, { useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Calendar,
  Search,
  Filter,
  MapPin,
  Clock,
  Trophy,
  ArrowRight,
  Sparkles,
  Swords,
  Gamepad2,
} from "lucide-react";
import { listEvents, listLeagues } from "../graphql/queries";
import {
  getLeagueGame,
  getLeagueGameLabel,
  LEAGUE_GAMES,
  LeagueGame,
} from "../lib/leagueGames";
import { formatDate } from "../utils/dateUtils";

const Events: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFormat, setSelectedFormat] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const client = generateClient();

  const { data: leagues } = useQuery({
    queryKey: ["eventLeagueOptions"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 100 },
      });
      return result.data.listLeagues.items;
    },
  });

  const currentLeague = leagues?.find((league: any) => league.id === leagueId);
  const currentGame = getLeagueGame(currentLeague?.game);

  const leagueByGame = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const currentSeasonLeagues = (leagues || [])
      .filter(
        (league: any) =>
          league.isActive &&
          league.startDate <= today &&
          league.endDate >= today
      )
      .sort((a: any, b: any) => b.startDate.localeCompare(a.startDate));

    return (Object.keys(LEAGUE_GAMES) as LeagueGame[]).reduce(
      (result, game) => {
        result[game] =
          currentSeasonLeagues.find(
            (league: any) => getLeagueGame(league.game) === game
          ) || null;
        return result;
      },
      {} as Record<LeagueGame, any>
    );
  }, [leagues]);

  // Fetch events for this league
  const {
    data: events,
    isLoading: eventsLoading,
    error: eventsError,
  } = useQuery({
    queryKey: ["leagueEvents", leagueId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: {
          filter: { leagueId: { eq: leagueId } },
          limit: 100,
        },
      });
      return result.data.listEvents.items;
    },
    enabled: !!leagueId,
  });

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    if (!events) return [];

    let filtered = events;

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (event: any) =>
          event.name.toLowerCase().includes(searchLower) ||
          event.location.toLowerCase().includes(searchLower) ||
          event.format.toLowerCase().includes(searchLower)
      );
    }

    // Filter by format
    if (selectedFormat !== "all") {
      filtered = filtered.filter(
        (event: any) =>
          event.format.toLowerCase() === selectedFormat.toLowerCase()
      );
    }

    // Filter by status
    if (selectedStatus !== "all") {
      const now = new Date();
      filtered = filtered.filter((event: any) => {
        const eventDate = new Date(event.date);
        switch (selectedStatus) {
          case "upcoming":
            return eventDate > now && !event.isCompleted;
          case "ongoing":
            return eventDate <= now && !event.isCompleted;
          case "completed":
            return event.isCompleted;
          default:
            return true;
        }
      });
    }

    // Sort by date (upcoming first, then completed)
    filtered.sort((a: any, b: any) => {
      const now = new Date();
      const aDate = new Date(a.date);
      const bDate = new Date(b.date);

      // If both are upcoming, sort by date ascending
      if (aDate > now && bDate > now) {
        return aDate.getTime() - bDate.getTime();
      }

      // If both are past, sort by date descending (most recent first)
      if (aDate <= now && bDate <= now) {
        return bDate.getTime() - aDate.getTime();
      }

      // Upcoming events come before past events
      return aDate > now ? -1 : 1;
    });

    return filtered;
  }, [events, searchTerm, selectedFormat, selectedStatus]);

  const getStatusBadge = (event: any) => {
    const now = new Date();
    const eventDate = new Date(event.date);

    if (event.isCompleted) {
      return <Badge variant="secondary">Completed</Badge>;
    } else if (eventDate > now) {
      return <Badge variant="default">Upcoming</Badge>;
    } else {
      return <Badge variant="outline">Ongoing</Badge>;
    }
  };

  if (eventsLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (eventsError) {
    return (
      <div className="text-center text-red-600">
        Error loading events: {eventsError.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge variant="outline">
              {getLeagueGameLabel(currentLeague?.game)}
            </Badge>
            {currentLeague && (
              <span className="text-sm text-muted-foreground">
                {currentLeague.name}
              </span>
            )}
          </div>
          <h1 className="text-3xl font-bold tracking-tight">
            {getLeagueGameLabel(currentLeague?.game)} Events
          </h1>
          <p className="text-muted-foreground">
            Browse and register for upcoming tournaments
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
        </div>
      </div>

      {/* Game selection */}
      <div className="grid gap-3 sm:grid-cols-3" role="tablist">
        {(Object.keys(LEAGUE_GAMES) as LeagueGame[]).map((game) => {
          const { label } = LEAGUE_GAMES[game];
          const Icon =
            game === "MAGIC_THE_GATHERING"
              ? Sparkles
              : game === "RIFTBOUND"
              ? Swords
              : Gamepad2;
          const gameLeague = leagueByGame[game];
          const isSelected = currentGame === game;

          return (
            <Button
              key={game}
              role="tab"
              aria-selected={isSelected}
              variant={isSelected ? "default" : "outline"}
              className="h-auto justify-start px-4 py-4"
              disabled={!gameLeague}
              onClick={() =>
                gameLeague && navigate(`/league/${gameLeague.id}/events`)
              }
            >
              <Icon className="mr-3 h-5 w-5" />
              <span className="text-left">
                <span className="block font-semibold">{label}</span>
                <span className="block text-xs opacity-75">
                  {gameLeague?.name || "No active league"}
                </span>
              </span>
            </Button>
          );
        })}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedFormat} onValueChange={setSelectedFormat}>
              <SelectTrigger>
                <SelectValue placeholder="All Formats" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Formats</SelectItem>
                {Array.from(
                  new Set(
                    (events || [])
                      .map((event: any) => event.format)
                      .filter(Boolean)
                  )
                )
                  .sort()
                  .map((format) => (
                    <SelectItem key={format as string} value={format as string}>
                      {format as string}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="ongoing">Ongoing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Events List */}
      <div className="space-y-4">
        {filteredEvents.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {searchTerm ||
              selectedFormat !== "all" ||
              selectedStatus !== "all"
                ? "No Events Match Your Filters"
                : "No Events Available"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ||
              selectedFormat !== "all" ||
              selectedStatus !== "all"
                ? "Try adjusting your search terms or filters."
                : "There are currently no events scheduled. Check back later for new tournaments."}
            </p>
            {(searchTerm ||
              selectedFormat !== "all" ||
              selectedStatus !== "all") && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedFormat("all");
                  setSelectedStatus("all");
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredEvents.map((event: any) => (
              <Card
                key={event.id}
                className="hover:shadow-md transition-shadow"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{event.name}</CardTitle>
                      <CardDescription>{event.description}</CardDescription>
                    </div>
                    {getStatusBadge(event)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>{formatDate(event.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{event.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                      <span>{event.format}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>Max Rounds: {event.maxRounds}</span>
                    </div>
                    {event.isPremium && (
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="secondary"
                          className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                        >
                          ⭐ Premium Event (2x Points)
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button asChild className="flex-1">
                      <Link to={`/event/${event.id}`}>
                        View Details
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
